// React is imported automatically in newer versions
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAllData } from '../lib/api';
import { getArtistNames } from '../utils/artistUtils';
import type { Artist, Voicebank, Song } from '../types/models';
import { SongCard } from '../components/SongCard';

export function Voicebank() {
  const { id } = useParams();
  const { data, isLoading } = useQuery(['appData'], fetchAllData);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-64 bg-white/5" />
        <div className="p-8">
          <div className="flex gap-8 mb-12">
            <div className="w-48 h-48 bg-white/10 rounded-xl" />
            <div className="flex-1">
              <div className="h-8 w-48 bg-white/10 rounded mb-4" />
              <div className="h-4 w-96 bg-white/10 rounded mb-2" />
              <div className="h-4 w-72 bg-white/10 rounded" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const voicebank = data.voicebanks.find((v: Voicebank) => v.id === id);
  if (!voicebank) return (
    <div className="p-8 text-center">
      <h2 className="font-outfit text-2xl text-pearl-white mb-4">Voicebank not found</h2>
      <p className="text-slate-grey mb-6">We couldn't find the voicebank you're looking for.</p>
      <Link to="/" className="text-naia-pink hover:text-soft-violet transition-colors">
        Return to Home
      </Link>
    </div>
  );

  // Get all songs using this voicebank
  const voicebankSongs = data.songs.filter((song: Song) => song.synthId === id);

  // Get unique artists using this voicebank
  const artistsUsingVoicebank = [...new Set(voicebankSongs.map((song: Song) => song.artistId))]
    .map((artistId) => data.artists.find((a: Artist) => a.id === artistId))
    .filter(Boolean);

  const getArtistName = (artistId: string) => {
    return getArtistNames(artistId, data.artists);
  };

  return (
    <div>
      <div className="p-8">
        {/* Voicebank Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12">
          <div className="w-48 h-48 flex-shrink-0 rounded-xl overflow-hidden border border-white/10 relative">
            {voicebank.imageUrl ? (
              <img 
                src={voicebank.imageUrl} 
                alt={voicebank.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-naia-pink/20 to-deep-night flex items-center justify-center">
                <span className="text-6xl font-outfit font-bold text-naia-pink opacity-50">
                  {voicebank.name.charAt(0)}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-outfit text-4xl font-bold text-pearl-white mb-4">
              {voicebank.name}
            </h1>
            <div className="flex flex-wrap gap-4 mb-6">
              <span className="px-3 py-1.5 bg-white/5 rounded-full text-sm text-slate-grey ring-1 ring-white/10">
                {voicebank.engine}
              </span>
              <span className="px-3 py-1.5 bg-white/5 rounded-full text-sm text-slate-grey ring-1 ring-white/10">
                {voicebank.version.toLowerCase().includes('ai') ? 'AI' : 'Standard'}
              </span>
              <span className="px-3 py-1.5 bg-white/5 rounded-full text-sm text-slate-grey ring-1 ring-white/10">
                {voicebank.language}
              </span>
            </div>
            <div className="space-y-4">
              <h3 className="text-lg text-pearl-white font-medium">Artists using this voicebank:</h3>
              <div className="flex flex-wrap gap-2">
                {artistsUsingVoicebank.map(artist => artist && (
                  <Link
                    key={artist.id}
                    to={`/artist/${artist.id}`}
                    className="px-3 py-1.5 bg-white/5 rounded-full text-sm text-naia-pink hover:bg-white/10 transition-colors ring-1 ring-white/10"
                  >
                    {artist.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Songs Section */}
        <section>
          <h2 className="font-outfit text-2xl font-semibold mb-6 flex items-center gap-3 relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
            Songs
            <span className="text-slate-grey text-sm font-normal">
              ({voicebankSongs.length})
            </span>
          </h2>
          {voicebankSongs.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {voicebankSongs.map((song: Song) => (
                <SongCard
                  key={song.id}
                  song={song}
                  artist={getArtistName(song.artistId)}
                  voicebank={voicebank.name}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-grey">
              No songs found using this voicebank yet.
            </div>
          )}
        </section>
      </div>
    </div>
  );
} 